/**
 * Wire DTOs for the team-management API surface.
 * `/api/team` GET/POST, `/api/team/[handle]` GET/PATCH/DELETE.
 */

export type Channel = "telegram" | "slack" | "teams";

export const CHANNELS: readonly Channel[] = ["telegram", "slack", "teams"] as const;

export function isChannel(v: unknown): v is Channel {
  return v === "telegram" || v === "slack" || v === "teams";
}

export interface TeamMemberIdentities {
  telegram?: string;
  slack?: string;
  teams?: string;
}

export interface TeamMember {
  handle: string;
  displayName: string;
  identities: TeamMemberIdentities;
  /** ISO 8601. Earliest addedAt across rows for this handle. */
  addedAt: string;
  /** Admin UPN who wrote the first row for this handle. */
  addedBy: string;
}

export interface ListTeamResponse {
  members: TeamMember[];
}

export interface CreateTeamRequest {
  handle: string;
  displayName: string;
  identities: TeamMemberIdentities;
}

export interface PatchTeamRequest {
  displayName?: string;
  addIdentities?: TeamMemberIdentities;
  removeIdentities?: Channel[];
}

export interface DeleteTeamResponse {
  deleted: { handle: string; rows: number };
}

/**
 * Partial-write failure shape returned in `UpstreamError.details.partial`
 * when a multi-identity POST fails after at least one row was already written.
 *
 * `created` is the subset of `TeamMemberIdentities` we successfully persisted.
 * `failed` is the single channel we stopped on (we abort on first failure;
 * see lib/team-members.ts).
 */
export interface PartialWriteDetails {
  created: TeamMemberIdentities;
  failed: {
    channel: Channel;
    channelUserId: string;
    originalMessage: string;
  };
}
